import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Spinner,
} from 'reactstrap';
import classnames from 'classnames';
import { DETAIL_TABS } from '../constants/tabs';
import { SubmitConfirmModal } from '../../../../../shared/offer-domain/ui';
import RequestImprovementModal from './Components/RequestImprovementModal';
import useRequestImprovement from '../../../hooks/useRequestImprovement';
import { shouldShowFreightTab } from '../../../utils/tenderEvaluationHelpers';

const EvaluationOperational = ({
  activeTab,
  onChangeTab,
  procId,
  procurement,
  isLoadingProcurement,
  onRefreshProcurement,
  parcels,
  isLoadingParcels,
  procurementDocuments,
  isLoadingDocuments,
}) => {
  const [selectedImprovements, setSelectedImprovements] = useState([]);
  const [selectionClearToken, setSelectionClearToken] = useState(0);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingForm, setPendingForm] = useState(null);
  const [visitedTabs, setVisitedTabs] = useState({ [activeTab]: true });
  const { submitBulkImprovement, isLoading: isSubmittingImprovement } =
    useRequestImprovement();
  const isSubmitting = isSubmittingImprovement;

  const showFreightTab = shouldShowFreightTab(procurement);
  const visibleTabs = DETAIL_TABS.filter(tab => {
    if (tab.id === 'freight') return showFreightTab;
    return true;
  });

  useEffect(() => {
    setVisitedTabs(prev =>
      prev[activeTab] ? prev : { ...prev, [activeTab]: true }
    );
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'freight' && !showFreightTab) {
      onChangeTab('Offer');
    }
  }, [activeTab, onChangeTab, showFreightTab]);

  const selectedCount = selectedImprovements.length;
  const showOfferActionBar = activeTab === 'Offer' && selectedCount > 0;

  const handleClearSelection = () => {
    setSelectionClearToken(token => token + 1);
    setSelectedImprovements([]);
  };

  const handleFormSubmit = form => {
    setPendingForm(form);
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    const success = await submitBulkImprovement({
      improvements: selectedImprovements,
      message: pendingForm?.message,
      closingDateImprovement: pendingForm?.closingDateImprovement,
      onSuccess: () => {
        setShowConfirmModal(false);
        setShowFormModal(false);
        setPendingForm(null);
        handleClearSelection();
        onRefreshProcurement?.();
      },
    });
    if (!success) return;
  };

  return (
    <>
      <h5 className='mb-3 fw-semibold'>Evaluation Operational</h5>
      <Card>
        <CardBody className='checkout-tab'>
          <Nav tabs className='nav-tabs-custom nav-primary mt-1 mb-3'>
            {visibleTabs.map(tab => (
              <NavItem key={tab.id}>
                <NavLink
                  className={classnames('cursor-pointer', {
                    active: activeTab === tab.id,
                  })}
                  onClick={() => onChangeTab(tab.id)}
                >
                  <i className={`${tab.icon} me-1 align-bottom`}></i>
                  <span className='fw-semibold'>{tab.label}</span>
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab}>
            {visibleTabs.map(tab => {
              if (!visitedTabs[tab.id]) return null;

              const TabComponent = tab.component;
              const sharedProps = {
                procId,
                procurement,
                isLoadingProcurement,
                onRefreshProcurement,
                parcels,
                isLoadingParcels,
              };
              const inviteProps = {
                procurementDocuments,
                isLoadingDocuments,
              };
              const offerProps =
                tab.id === 'Offer'
                  ? {
                      onSelectedOffersChange: setSelectedImprovements,
                      selectionClearToken,
                      isActive: activeTab === tab.id,
                    }
                  : {};
              const attendanceProps =
                tab.id === 'attendance'
                  ? { isActive: activeTab === tab.id }
                  : {};

              return (
                <TabPane key={tab.id} tabId={tab.id}>
                  <TabComponent
                    {...sharedProps}
                    {...(tab.id === 'invite-dmut' || tab.id === 'invite-panitia'
                      ? inviteProps
                      : {})}
                    {...offerProps}
                    {...attendanceProps}
                  />
                </TabPane>
              );
            })}
          </TabContent>

          {showOfferActionBar ? (
            <div className='req-actionbar'>
              <span className='me-auto fw-semibold text-dark align-self-center'>
                {selectedCount} Offer Selected
              </span>
              <Button
                color='light'
                type='button'
                className='d-flex align-items-center gap-2'
                disabled={isSubmitting}
                onClick={handleClearSelection}
              >
                <span>Cancel</span>
              </Button>
              <Button
                color='primary'
                type='button'
                className='d-flex align-items-center gap-2'
                disabled={isSubmitting}
                onClick={() => setShowFormModal(true)}
              >
                {isSubmitting ? (
                  <Spinner size='sm' />
                ) : (
                  <i className='ri-send-plane-fill'></i>
                )}
                <span>Send Request Improvement</span>
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <RequestImprovementModal
        isOpen={showFormModal}
        onClose={() => {
          if (isSubmitting) return;
          setShowFormModal(false);
          setPendingForm(null);
        }}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        selectedCount={selectedCount}
      />

      <SubmitConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          if (isSubmitting) return;
          setShowConfirmModal(false);
        }}
        onConfirm={handleConfirmSubmit}
        title='Confirm Request Improvement'
        message='Are you sure you want to submit this request improvement of offering to registered supplier? This action cannot be undone.'
        cancelLabel='Back'
        confirmLabel='Submit'
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default EvaluationOperational;
